// React Query Hooks
import { useQuery } from "react-query";

import axios from "axios";
import { nuclearn_api_url } from "src/App";
import { shouldNotRetry404 } from "src/config/react-query";
import { useProvenanceV2Ready } from "./ProvenanceService";

export interface ArtifactResponse {
  id: string;
  name: string;
  content: string;
  type: string;
  created_at: string;
  updated_at: string;
}

export interface WorkflowArtifactResult {
  artifact_id: number;
  artifact_name: string;
  text_report_id?: number;
  text_report_name?: string;
  message: string;
}

/** One generated reference number mapped back to its source passage (#20423). */
export interface SourceProvenanceReference {
  n: number;
  chunk_id?: number | null;
  source_data_record_id?: number | null;
  document_name?: string | null;
  page?: number | number[] | null;
  dataset_id?: number | null;
  /**
   * Folder id for a project-file-backed reference (MRE #30376). Scope is
   * mutually exclusive with dataset_id (`ck_sdr_scope_xor`); absent on older
   * manifests and blanked on redacted refs.
   */
  project_folder_id?: number | null;
  /** Indices into {@link ArtifactSourceProvenance.claims} this reference supports. */
  supports_claims?: number[];
}

/** One sourced claim mapped to the references it cites (#20423 Story 6). */
export interface SourceProvenanceClaim {
  claim_text: string;
  chunk_ids?: number[];
  /** 1-based reference numbers (SourceProvenanceReference.n) this claim cites. */
  cite_nums?: number[];
}

/** Provenance manifest for a generated document (#20423 Story 5/6). */
export interface ArtifactSourceProvenance {
  claim_count: number;
  document_count: number;
  references: SourceProvenanceReference[];
  /** Claim-level mapping; empty/absent on legacy manifests. */
  claims?: SourceProvenanceClaim[];
}

export class ArtifactService {
  /**
   * Fetch artifact content by ID using the content endpoint for display
   */
  static async getArtifactContent(
    artifactId: string | number,
  ): Promise<string> {
    try {
      const response = await axios.get(
        `${nuclearn_api_url}/artifacts/${artifactId}/content`,
        {
          responseType: "text", // Ensure we get the content as text
          headers: {
            Accept: "text/plain, text/markdown, */*", // Accept text content
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch artifact ${artifactId}:`, error);
      throw new Error(`Failed to load artifact content: ${error}`);
    }
  }

  /**
   * Update artifact content by ID
   */
  static async updateArtifactContent(
    artifactId: string | number,
    content: string,
    updateFilename?: string,
  ): Promise<void> {
    try {
      const payload = {
        content: content,
        content_type: "text/plain",
        ...(updateFilename && { update_filename: updateFilename }),
      };

      await axios.put(
        `${nuclearn_api_url}/artifacts/${artifactId}/content`,
        payload,
        {
          headers: { "Content-Type": "application/json" },
        },
      );
    } catch (error) {
      console.error(`Failed to update artifact ${artifactId}:`, error);
      throw new Error(`Failed to save artifact changes: ${error}`);
    }
  }

  /**
   * Get artifact metadata by ID
   */
  static async getArtifactMetadata(
    artifactId: string | number,
  ): Promise<ArtifactResponse> {
    try {
      const response = await axios.get(
        `${nuclearn_api_url}/artifacts/${artifactId}`,
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch artifact metadata ${artifactId}:`, error);
      throw new Error(`Failed to load artifact metadata: ${error}`);
    }
  }

  /**
   * Fetch the source-provenance manifest for a generated document (#20423).
   * The backend returns an EMPTY manifest (200) for artifacts with no sourced
   * claims; a 404 means a legacy backend without the route — also a normal
   * "no manifest" state. Every other error PROPAGATES so react-query retries
   * and records the error — a transient failure must not be cached as a
   * successful empty manifest for 5 minutes (#20423 audit 11).
   */
  static async getArtifactProvenance(
    artifactId: string | number,
  ): Promise<ArtifactSourceProvenance> {
    try {
      const response = await axios.get(
        `${nuclearn_api_url}/artifacts/${artifactId}/provenance`,
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return { claim_count: 0, document_count: 0, references: [] };
      }
      throw error;
    }
  }

  /**
   * Download artifact as PDF from backend (uses Gotenberg for conversion)
   */
  static async downloadArtifactAsPdf(
    artifactId: string | number,
    filename?: string,
  ): Promise<void> {
    try {
      const response = await axios.get(
        `${nuclearn_api_url}/artifacts/${artifactId}/download-as-pdf`,
        {
          responseType: "blob",
        },
      );

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download =
        filename?.replace(/\.md$/, ".pdf").replace(/\.txt$/, ".pdf") ||
        `artifact_${artifactId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(`Failed to download artifact ${artifactId} as PDF:`, error);
      throw new Error(`Failed to generate PDF: ${error}`);
    }
  }

  /**
   * Get the URL to view artifact as PDF inline in browser
   */
  static getArtifactPdfUrl(artifactId: string | number): string {
    return `${nuclearn_api_url}/artifacts/${artifactId}/download-as-pdf`;
  }

  /**
   * Check if a message content contains workflow artifact results
   */
  static isWorkflowArtifactResult(content: string): boolean {
    try {
      const parsed = JSON.parse(content);
      return (
        typeof parsed === "object" &&
        parsed !== null &&
        "artifact_id" in parsed &&
        "message" in parsed
      );
    } catch {
      return false;
    }
  }

  /**
   * Parse workflow artifact result from message content
   */
  static parseWorkflowArtifactResult(
    content: string,
  ): WorkflowArtifactResult | null {
    try {
      const parsed = JSON.parse(content);
      if (this.isWorkflowArtifactResult(content)) {
        return parsed as WorkflowArtifactResult;
      }
      return null;
    } catch {
      return null;
    }
  }
}

/**
 * Hook to fetch artifact content by ID
 */
export const useArtifactContent = (artifactId: string | number | null) => {
  return useQuery(
    ["artifact-content", artifactId],
    () => ArtifactService.getArtifactContent(artifactId!),
    {
      enabled: !!artifactId,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    },
  );
};

/**
 * Hook to fetch a generated document's source-provenance manifest (#20423).
 * Capability-gated like the message/dialogue provenance hooks — a legacy
 * backend is never queried per file row (#20423 audit 11) — and 404s fail
 * fast instead of burning the retry budget.
 */
export const useArtifactProvenance = (artifactId: string | number | null) => {
  const v2Ready = useProvenanceV2Ready();
  return useQuery(
    ["artifact-provenance", artifactId],
    () => ArtifactService.getArtifactProvenance(artifactId!),
    {
      enabled: !!artifactId && v2Ready,
      retry: shouldNotRetry404,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    },
  );
};
